import classNames from 'classnames';
import { motion } from 'motion/react';
import { ComponentProps } from 'react';

export const WizardSteps = ({
  currentStep,
  steps,
}: {
  currentStep: number;
  steps: { label: string }[];
}) => {
  return (
    <>
      <nav
        aria-label="Wizard progress"
        className="mb-5 flex items-start rounded-lg px-0 py-3 sm:px-6"
      >
        {steps.map((step, index) => {
          const status =
            currentStep === index ? 'active' : currentStep < index ? 'inactive' : 'complete';
          return (
            <div
              key={step.label}
              aria-current={status === 'active' ? 'step' : undefined}
              className="relative flex min-w-0 flex-1 flex-col items-center"
            >
              {index > 0 && (
                <div
                  aria-hidden="true"
                  data-wizard-step-connector
                  className={classNames(
                    'absolute top-3 right-1/2 h-0.5 w-full sm:top-5',
                    index <= currentStep ? 'bg-primary-50' : 'bg-grey-800'
                  )}
                />
              )}
              <motion.div animate={status} className="relative z-1" data-wizard-step-marker>
                <motion.div
                  variants={{
                    active: {
                      scale: 1,
                      transition: {
                        delay: 0,
                        duration: 0.2,
                      },
                    },
                    complete: {
                      scale: 1.25,
                    },
                  }}
                  transition={{
                    duration: 0.6,
                    delay: 0.2,
                    type: 'tween',
                    ease: 'circOut',
                  }}
                  className="absolute inset-0 rounded-full"
                />
                <motion.div
                  initial={false}
                  variants={{
                    inactive: {
                      backgroundColor: '#fff',
                      borderColor: '#ccced0',
                      color: '#9a9da1',
                    },
                    active: {
                      backgroundColor: '#fff',
                      borderColor: '#185ec8',
                      color: '#185ec8',
                    },
                    complete: {
                      backgroundColor: '#185ec8',
                      color: '#185ec8',
                    },
                  }}
                  transition={{ duration: 0.2 }}
                  className="relative flex h-6 w-6 items-center justify-center rounded-full border-2 text-xs font-semibold sm:h-10 sm:w-10 sm:text-base"
                >
                  <div className="flex items-center justify-center">
                    {status === 'complete' ? (
                      <CheckIcon className="h-4 w-4 text-white sm:h-6 sm:w-6" />
                    ) : (
                      <div>
                        <span>{index + 1}</span>
                      </div>
                    )}
                  </div>
                </motion.div>
              </motion.div>
              <span
                data-wizard-step-label
                className="sr-only mt-2 text-center text-sm font-medium leading-tight text-grey-300 sm:not-sr-only sm:block sm:w-full sm:px-1"
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </nav>
    </>
  );
};

function CheckIcon(props: ComponentProps<'svg'>) {
  return (
    <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
      <motion.path
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{
          delay: 0.2,
          type: 'tween',
          ease: 'easeOut',
          duration: 0.3,
        }}
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
}
